import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AvatarInput,
  Button,
  ButtonsWrapper,
  Form,
  ICONS,
  PLATFORM_THEME_COLORS,
  Separator,
  SwitchInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { DatevNumberStatusesEnum } from '@thinknet-gmbh/platform-common';

import { useGetAllDatevNumbers } from 'src/hooks/datev-numbers/get-datev-numbers.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import { DEFAULT_COMPANY_TO_DELETE } from 'src/pages/settings/components/company-profile/company-profile.constants';
import { DeleteSubsidiaryCompany } from 'src/pages/settings/components/company-profile/components/delete-subsidiary-company';

import { ICompanyCommonFormProps } from './company-common-form.interfaces';
import { HeaderContainer, MainWrapper, StyledSwitchButton } from './company-common-form.styles';
import { GeneralInformationForm } from './components/general-information-form';
import { LocationFormList } from './components/location-form-list';
import { getCompanyFormTitle, getFormInitialValues } from './company-common-form.helpers';
import { getTimezoneValue } from '../../../../../../common/helpers/common.helpers';

const CompanyCommonForm = ({ companyData, onCancel, onFinish }: ICompanyCommonFormProps) => {
  const { t } = useTranslation(['common', 'settings']);
  const [form] = Form.useForm();
  const [isAddedNewLocation, setIsAddedNewLocation] = useState(false);
  const [logo, setLogo] = useState<File | string | undefined>(companyData.logoUrl);
  const timezoneValue = getTimezoneValue(companyData);
  const [timezone, setTimezone] = useState<string>(timezoneValue);
  const [timezoneOffset, setTimezoneOffset] = useState<number>(companyData?.timezone || 120);
  const navigate = useNavigate();

  const [companyToDelete, setCompanyToDelete] = useState(DEFAULT_COMPANY_TO_DELETE);
  const [companyDatevNumberId, setCompanyDatevNumberId] = useState('');
  const [isCanUseDatevNumber, setIsCanUseDatevNumber] = useState(!!companyData.datevNumber);

  const currentCompanyId = useGetCurrentCompanyId();
  const { collection: datevNumbers } = useGetAllDatevNumbers(
    currentCompanyId,
    DatevNumberStatusesEnum.FREE
  );

  useEffect(() => {
    setIsAddedNewLocation(false);
  }, [isAddedNewLocation]);

  const onFinishHandler = (values: any) => {
    onFinish({ ...values, logo, timezone: Number(timezoneOffset), timezoneFullFormat: timezone });
  };

  const onTimeZoneSelect = (value: string, options: any) => {
    if (options?.dataoffset) {
      setTimezoneOffset(options.dataoffset);
    }
    setTimezone(value);
  };

  const onCancelHandler = () => {
    onCancel();
    form.resetFields();
  };

  return (
    <MainWrapper>
      {companyToDelete._id && <DeleteSubsidiaryCompany companyToDelete={companyToDelete} />}
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t(`settings:company.${getCompanyFormTitle(companyData)}`)}
        </Typography>
        <div>
          {companyData._id && companyData.parentCompanyId && (
            <Button
              type="text"
              icon={ICONS.trash}
              iconColor={PLATFORM_THEME_COLORS.tone.red}
              onClick={() => setCompanyToDelete({ _id: companyData._id, name: companyData.name })}
            >
              {t('settings:company.buttons.deleteCompany')}
            </Button>
          )}
          <StyledSwitchButton>
            <SwitchInput
              defaultChecked={isCanUseDatevNumber}
              onChange={checked => {
                setIsCanUseDatevNumber(checked);
                if (!checked) {
                  const locations = form.getFieldValue('locations');

                  form.setFieldsValue({
                    datevNumberId: null,
                    locations: locations.map((item: any) => ({ ...item, datevNumberId: null })),
                  });
                }
              }}
            />
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {t('settings:datev.labels.datevNumber')}
            </Typography>
          </StyledSwitchButton>
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setIsAddedNewLocation(true)}
          >
            {t('settings:company.buttons.addNewLocation')}
          </Button>
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() =>
              navigate(`${ROUTES.settings}/${SETTINGS_PAGES.addSubsidiaryCompany}`, {
                state: { parentCompanyId: companyData._id },
              })
            }
          >
            {t('settings:company.buttons.addSubsidiaryCompany')}
          </Button>
        </div>
      </HeaderContainer>
      <AvatarInput
        onChange={logo => setLogo(logo)}
        avatar={logo}
        btnText={t('common:buttons.uploadLogo')}
        squareForm={true}
      />
      <Form
        layout="vertical"
        onFinish={onFinishHandler}
        initialValues={getFormInitialValues({ ...companyData, timezone: timezoneValue })}
        form={form}
      >
        <GeneralInformationForm
          canAddDatevNumber={isCanUseDatevNumber}
          setCopmanyDatevNumber={setCompanyDatevNumberId}
          datevNumbers={datevNumbers}
          editCompany={companyData}
          timezone={timezone}
          onTimeZoneSelect={onTimeZoneSelect}
          isSubCompany={!companyData || companyData.parentCompanyId}
        />
        <Separator />
        <Form.List name="locations">
          {(fields, { add, remove }) => (
            <LocationFormList
              canAddDatevNumber={isCanUseDatevNumber}
              companyDatevNumberId={companyDatevNumberId}
              add={add}
              isAddedNewLocation={isAddedNewLocation}
              fields={fields}
              remove={remove}
              form={form}
              datevNumbers={datevNumbers}
              editCompany={companyData}
            />
          )}
        </Form.List>
        <ButtonsWrapper marginTop={18}>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onCancelHandler}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </MainWrapper>
  );
};

export default CompanyCommonForm;
